import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { unifiedWebSocketService } from '../services/unifiedWebSocket';
import { useOptimizationStore } from '../store/optimizationStore';
import HelpTourButton from './guidedTour/HelpTourButton';

/**
 * Определяет URL чата на основе текущего окружения
 * QA → qa.chat.infiano.app
 * Dev → dev.chat.infiano.app
 * Prod → chat.infiano.app
 */
const getChatUrl = (): string => {
  const hostname = window.location.hostname.toLowerCase();
  
  if (hostname.includes('qa')) {
    return 'https://qa.chat.infiano.app';
  } else if (hostname.includes('dev')) {
    return 'https://dev.chat.infiano.app';
  } else {
    // Production по умолчанию
    return 'https://chat.infiano.app';
  }
};

interface HeaderProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
  onHelpClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLoginClick, onRegisterClick, onHelpClick }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 787);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { isAuthenticated, user, logout, isLoading } = useAuthContext();
  const isAdmin = Boolean(
    isAuthenticated &&
    user &&
    typeof (user as any).role === 'string' &&
    ((user as any).role as string).toLowerCase() === 'admin'
  );
  const navigate = useNavigate();
  const location = useLocation();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const mobileProfileMenuRef = useRef<HTMLDivElement>(null);
  const burgerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 787);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Закрытие меню профиля при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Важно: мы рендерим И мобильный, И десктопный хедер одновременно и прячем их через CSS.
      // Поэтому закрываем меню только если клик был ВНЕ обоих контейнеров.
      const clickedInsideDesktop = Boolean(profileMenuRef.current?.contains(target));
      const clickedInsideMobile = Boolean(mobileProfileMenuRef.current?.contains(target));

      if (!clickedInsideDesktop && !clickedInsideMobile) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  // Закрытие burger menu при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (burgerMenuRef.current && !burgerMenuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/connector');
    } else {
      navigate('/');
    }
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
    setMenuOpen(false);
    setProfileMenuOpen(false);
  };

  const handleOptimizeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAuthenticated) {
      navigate('/connector');
      setTimeout(() => {
        const connectorConfig = document.getElementById('connector-config-section');
        if (connectorConfig) {
          connectorConfig.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 120);
    } else {
      navigate('/');
      setTimeout(() => {
        const configureSection = document.getElementById('configure-section');
        if (configureSection) {
          configureSection.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
    setMenuOpen(false);
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Переход на InfianoChat в текущей вкладке
    window.location.href = getChatUrl();
    setMenuOpen(false);
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setMenuOpen(false);
  };

  const scrollToConfigure = (e: React.MouseEvent) => {
    e.preventDefault();
    const configureSection = document.getElementById('configure-section');
    if (configureSection) {
      configureSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return; // Предотвращаем повторные клики
    
    setIsLoggingOut(true);
    try {
      // Останавливаем оптимизацию, если она запущена
      const { 
        isOptimizing, 
        multiAgentIsOptimizing, 
        setIsOptimizing, 
        setMultiAgentIsOptimizing,
        setOptimizationProgress,
        setMultiAgentOptimizationProgress
      } = useOptimizationStore.getState();
      
      if (isOptimizing || multiAgentIsOptimizing) {
        console.log('🛑 Stopping optimization before logout...');
        
        // Отключаем WebSocket соединение и очищаем сессию
        unifiedWebSocketService.disconnect(true); // clearSession = true
        
        // Сбрасываем состояние оптимизации
        setIsOptimizing(false);
        setMultiAgentIsOptimizing(false);
        setOptimizationProgress(0);
        setMultiAgentOptimizationProgress(0);
        
        console.log('✅ Optimization stopped successfully');
      }
      
      await logout();
      setMenuOpen(false);
      setProfileMenuOpen(false);
      navigate('/'); // Редирект на главную после выхода
    } catch (error) {
      console.error('Logout error:', error);
      // Даже при ошибке закрываем меню
      setMenuOpen(false);
      setProfileMenuOpen(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setProfileMenuOpen(false); // Закрываем профиль меню при открытии бургер меню
  };

  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
    setMenuOpen(false); // Закрываем бургер меню при открытии профиль меню
  };

  return (
    <header className={`header${profileMenuOpen || menuOpen ? ' header--menu-open' : ''}`}>
      <div className="logo" onClick={handleLogoClick}>
        <img src="/images/intellemma-logo.png" alt="intellemma" className="logo-image" />
        <span className="logo-sep" aria-hidden="true"></span>
        <span className="logo-product">AI Tuner</span>
      </div>
      {/*
        Render both desktop and mobile controls and rely on CSS media queries to show/hide.
        This avoids cases where JS viewport width differs from CSS media queries (e.g. device zoom/viewport quirks),
        which can result in "missing" navigation on mobile.
      */}
      <div className="mobile-header-controls">
        {/* Профиль меню */}
        {isLoading ? (
          // Placeholder to prevent flicker while auth is initializing
          <div style={{ width: 44, height: 44 }} />
        ) : isAuthenticated ? (
          <div className="mobile-profile-container" ref={mobileProfileMenuRef}>
            <button className="mobile-profile-icon-btn" onClick={toggleProfileMenu}>
              <i className="fas fa-user-circle mobile-profile-icon" style={{ fontSize: 28, color: '#0099FA' }}></i>
            </button>
            {profileMenuOpen && (
              <div className="mobile-profile-dropdown-menu">
                <button 
                  className="profile-menu-btn styled-profile-btn" 
                  onClick={() => { setProfileMenuOpen(false); navigate('/profile'); }}
                >
                  Профиль
                </button>
                <button 
                  className="profile-menu-btn styled-logout-btn" 
                  onClick={handleLogout} 
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Выходим…' : 'Выйти'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="mobile-auth-button" onClick={onLoginClick}>
            Войти
          </button>
        )}

        {/* Бургер меню (держим справа, чтобы дропдаун ровно прижимался к краю экрана) */}
        <div className="mobile-menu-container" ref={burgerMenuRef}>
          <button className="burger-menu" onClick={toggleMenu} aria-label="Открыть меню">
            <span className="burger-bar"></span>
            <span className="burger-bar"></span>
            <span className="burger-bar"></span>
          </button>
          {menuOpen && (
            <div className="mobile-dropdown-menu">
              {isAuthenticated && <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/multi-agent'); setMenuOpen(false); }}>Мультиагент</a>}
              {isAuthenticated && <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/connector'); setMenuOpen(false); }}>Коннектор</a>}
              <a href="#" className="nav-link" onClick={handleChatClick}>InfianoChat</a>
              <a href="#" className="nav-link" onClick={handleOptimizeClick}>Оптимизация</a>
              {isAdmin && (
                <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/admin'); setMenuOpen(false); }}>
                  Админка
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <nav className="nav">
        {isAuthenticated && (
          <a href="#" className={`nav-link${location.pathname === '/multi-agent' ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); navigate('/multi-agent'); setMenuOpen(false); }}>Мультиагент</a>
        )}
        {isAuthenticated && (
          <a href="#" className={`nav-link${location.pathname === '/connector' ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); navigate('/connector'); setMenuOpen(false); }}>Коннектор</a>
        )}
        <a href="#" className="nav-link" onClick={handleChatClick}>InfianoChat</a>
        {!isAuthenticated && <a href="#" className="nav-link" onClick={handleOptimizeClick}>Оптимизация</a>}
        {onHelpClick ? (
          <HelpTourButton compact onClick={onHelpClick} className="header-help-tour-btn" />
        ) : null}
        {isAdmin && (
          <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/admin'); setMenuOpen(false); }}>
            Админка
          </a>
        )}
        {isLoading ? (
          // Placeholder to keep layout stable while checking auth
          <div style={{ width: 96, height: 36 }} />
        ) : isAuthenticated ? (
          <div className="profile-menu-container" ref={profileMenuRef}>
            <button className="profile-icon-btn" onClick={() => setProfileMenuOpen(!profileMenuOpen)}>
              <i className="fas fa-user-circle profile-icon" style={{ fontSize: 28, color: '#0099FA' }}></i>
            </button>
            {profileMenuOpen && (
              <div className="profile-dropdown-menu">
                <button 
                  className="profile-menu-btn styled-profile-btn" 
                  onClick={() => { setProfileMenuOpen(false); navigate('/profile'); }}
                >
                  Профиль
                </button>
                <button 
                  className="profile-menu-btn styled-logout-btn" 
                  onClick={handleLogout} 
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Выходим…' : 'Выйти'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="auth-button" onClick={onLoginClick}>
            Войти
          </button>
        )}
      </nav>
    </header>
  );
};

export default Header; 